import React, { useContext, useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../../jsx/layouts/PageTitle";
import { AuthContext } from "../../../context/AuthContext";
import { getUddyamaanAssessments, sendUddyamaanApplicationsForward, selectUddyamaanApplications } from "../../../services/adminApiService/uddyamaanApi/uddyamaanApi";
import AssessmentFormModal from "./AssessmentFormModal";
import AssessmentHistoryModal from "./AssessmentHistoryModal";
import ConfirmModal from "../editProfile/ConfirmModal";
import swal from "sweetalert";

const STAGE_LABELS = { 7: "Nyaya Panchayat", 6: "Block", 8: "Local Body", 3: "District" };

const calculateAge = (dob) => {
  if (!dob) return "—";
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

const SCHEME_LABEL = { uddyamaan: 'Udiyamaan Unnayan Yojna', protsaahan: 'Protsaahan' };

const UddyamaanAssessmentList = ({ schemeType = 'uddyamaan' }) => {
  const navigate = useNavigate();
  const schemeLabel = SCHEME_LABEL[schemeType] || SCHEME_LABEL.uddyamaan;
  const { userData } = useContext(AuthContext);
  const userLevel = Number(userData?.user_level);
  const canForward = userLevel === 6 || userLevel === 7 || userLevel === 8;
  const canSelect = userLevel === 3;
  const isFullAdmin = userLevel === 2;
  const stageLabel = STAGE_LABELS[userLevel];

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 10;
  const [search, setSearch] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [ageRangeFilter, setAgeRangeFilter] = useState("");
  const [assessmentStatusFilter, setAssessmentStatusFilter] = useState("");
  const [selectedApp, setSelectedApp] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showForwardConfirm, setShowForwardConfirm] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const [showSelectConfirm, setShowSelectConfirm] = useState(false);
  const [selecting, setSelecting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [currentPage, search, genderFilter, ageRangeFilter, assessmentStatusFilter, schemeType]);

  useEffect(() => {
    setSelectedIds([]);
  }, [applications]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const forwardableApps = applications.filter(app =>
    !!app.assessment_result && (!canSelect || app.status !== "selected")
  );
  const allForwardableSelected = forwardableApps.length > 0 && forwardableApps.every(app => selectedIds.includes(app.id));

  const toggleSelectAll = () => {
    if (allForwardableSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(forwardableApps.map(app => app.id));
    }
  };

  const handleSendForward = async () => {
    setForwarding(true);
    try {
      const res = await sendUddyamaanApplicationsForward(selectedIds);
      const data = res?.data?.data || {};
      const failedEntries = Object.entries(data.failed || {});
      if (failedEntries.length > 0) {
        const details = failedEntries.map(([id, reason]) => `#${id}: ${reason}`).join("\n");
        swal("Some applications were not forwarded", details, "warning");
      } else {
        swal("Forwarded", "Selected applications were sent forward successfully.", "success");
      }
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      swal("Error", err?.response?.data?.message || "Failed to send applications forward.", "error");
    } finally {
      setForwarding(false);
    }
  };

  const handleSelectApplications = async () => {
    setSelecting(true);
    try {
      const res = await selectUddyamaanApplications(selectedIds);
      const data = res?.data?.data || {};
      const failedEntries = Object.entries(data.failed || {});
      if (failedEntries.length > 0) {
        const details = failedEntries.map(([id, reason]) => `#${id}: ${reason}`).join("\n");
        swal("Some applications were not selected", details, "warning");
      } else {
        swal("Selected", "Selected applications have been marked as selected.", "success");
      }
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      swal("Error", err?.response?.data?.message || "Failed to select applications.", "error");
    } finally {
      setSelecting(false);
    }
  };

  async function fetchData() {
    setLoading(true);
    try {
      const res = await getUddyamaanAssessments({ page: currentPage, search, gender: genderFilter, ageRange: ageRangeFilter, assessmentStatus: assessmentStatusFilter, schemeType });
      setApplications(res?.data?.data?.data || []);
      setTotal(res?.data?.data?.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const handlePageChange = (event) => {
    setCurrentPage(event.selected + 1);
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await getUddyamaanAssessments({ page: 1, search, perPage: 5000, gender: genderFilter, ageRange: ageRangeFilter, assessmentStatus: assessmentStatusFilter, schemeType });
      const rows = res?.data?.data?.data || [];
      const headers = [
        "#", "Name", "Email", "Program", "District", "DOB", "Approved Date",
        "Shuttle Run (sec)", "Flying Run (sec)", "600m Run (min)", "600m Run (sec)",
        "Broad Jump (cm)", "Medicine Ball (m)", "Medicine Ball (cm)", "Forward Bend (cm)", "Notes",
      ];
      const csvRows = [
        headers.join(","),
        ...rows.map((app, idx) => {
          const ar = app.assessment_result || {};
          return [
            idx + 1,
            `"${(app.name || "").replace(/"/g, '""')}"`,
            `"${(app.user?.email || "").replace(/"/g, '""')}"`,
            `"${(app.program?.title || "").replace(/"/g, '""')}"`,
            `"${(app.district || "").replace(/"/g, '""')}"`,
            app.dob ? new Date(app.dob).toLocaleDateString("en-IN") : "",
            app.updated_at ? new Date(app.updated_at).toLocaleDateString("en-IN") : "",
            ar.shuttle_run_seconds ?? "",
            ar.flying_run_seconds ?? "",
            ar.run_600m_minutes ?? "",
            ar.run_600m_seconds ?? "",
            ar.broad_jump_cm ?? "",
            ar.medicine_ball_meters ?? "",
            ar.medicine_ball_cm ?? "",
            ar.forward_bend_cm ?? "",
            `"${(ar.notes || "").replace(/"/g, '""')}"`,
          ].join(",");
        }),
      ];
      const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${schemeType}_assessments.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Assessment Queue" motherMenu={schemeLabel} />
      <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <h2>{schemeLabel} — Assessment Queue</h2>
        {stageLabel && (
          <div className="text-muted mb-2" style={{ fontSize: 13 }}>Assessing at: {stageLabel} level</div>
        )}

        <div className="row g-2 mb-4 mt-2">
          <div className="col-12 col-md">
            <div style={{ position: "relative" }}>
              <input
                type="text"
                placeholder="Search by name or district..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="form-control"
                style={{ padding: "10px 40px 10px 16px", borderRadius: 8, fontSize: 16 }}
              />
              <span style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", color: "#888" }}>
                <i className="fa fa-search" />
              </span>
            </div>
          </div>
          <div className="col-12 col-md-auto">
            <select
              value={ageRangeFilter}
              onChange={(e) => { setAgeRangeFilter(e.target.value); setCurrentPage(1); }}
              className="form-select"
              style={{ padding: "10px 40px 10px 12px" }}
            >
              <option value="">All Ages</option>
              <option value="8-9">8 to 9 years</option>
              <option value="9-10">9 to 10 years</option>
              <option value="10-11">10 to 11 years</option>
              <option value="11-12">11 to 12 years</option>
              <option value="12-13">12 to 13 years</option>
              <option value="13-14">13 to 14 years</option>
            </select>
          </div>
          <div className="col-12 col-md-auto">
            <select
              value={genderFilter}
              onChange={(e) => { setGenderFilter(e.target.value); setCurrentPage(1); }}
              className="form-select"
              style={{ padding: "10px 40px 10px 12px" }}
            >
              <option value="">All Genders</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
          <div className="col-12 col-md-auto">
            <select
              value={assessmentStatusFilter}
              onChange={(e) => { setAssessmentStatusFilter(e.target.value); setCurrentPage(1); }}
              className="form-select"
              style={{ padding: "10px 40px 10px 12px" }}
            >
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="selected">Selected</option>
            </select>
          </div>
          {canForward && (
            <div className="col-12 col-md-auto">
              <button
                className="btn"
                onClick={() => setShowForwardConfirm(true)}
                disabled={selectedIds.length === 0 || forwarding}
                style={{ background: "#4338CA", color: "#fff", borderRadius: 8, padding: "10px 20px", fontWeight: 500, whiteSpace: "nowrap" }}
              >
                {forwarding ? "Forwarding..." : `Send Forward${selectedIds.length ? ` (${selectedIds.length})` : ""}`}
              </button>
            </div>
          )}
          {canSelect && (
            <div className="col-12 col-md-auto">
              <button
                className="btn"
                onClick={() => setShowSelectConfirm(true)}
                disabled={selectedIds.length === 0 || selecting}
                style={{ background: "#B7791F", color: "#fff", borderRadius: 8, padding: "10px 20px", fontWeight: 500, whiteSpace: "nowrap" }}
              >
                {selecting ? "Selecting..." : `Selected${selectedIds.length ? ` (${selectedIds.length})` : ""}`}
              </button>
            </div>
          )}
          <div className="col-12 col-md-auto">
            <button
              className="btn"
              onClick={handleExportCSV}
              disabled={exporting}
              style={{ background: "#7C3AED", color: "#fff", borderRadius: 8, padding: "10px 20px", fontWeight: 500, whiteSpace: "nowrap" }}
            >
              {exporting ? "Exporting..." : "⬇ Export CSV"}
            </button>
          </div>
        </div>

        <div className="card-body">
          {loading ? (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : (
            <div className="w-100 table-responsive">
              <table className="display w-100 dataTable table">
                <thead>
                  <tr>
                    {(canForward || canSelect) && (
                      <th>
                        <input
                          type="checkbox"
                          checked={allForwardableSelected}
                          onChange={toggleSelectAll}
                          disabled={forwardableApps.length === 0}
                        />
                      </th>
                    )}
                    <th>#</th>
                    <th>Name</th>
                    <th className="d-none d-md-table-cell">Program</th>
                    <th className="d-none d-md-table-cell">District</th>
                    <th className="d-none d-md-table-cell">Age</th>
                    <th className="d-none d-md-table-cell">Gender</th>
                    <th className="d-none d-md-table-cell">Total Marks</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.length > 0 ? (
                    applications.map((app, idx) => (
                      <tr key={app.id}>
                        {(canForward || canSelect) && (
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(app.id)}
                              disabled={!app.assessment_result || (canSelect && app.status === "selected")}
                              onChange={() => toggleSelect(app.id)}
                            />
                          </td>
                        )}
                        <td>{(currentPage - 1) * perPage + idx + 1}</td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{app.name}</div>
                          <div style={{ color: "#9CA3AF", fontSize: 12 }}>{app.user?.email || ""}</div>
                          <div className="d-md-none text-muted" style={{ fontSize: 11 }}>
                            {[app.program?.title, app.district,
                              app.dob ? `${calculateAge(app.dob)} yrs` : null,
                              app.gender,
                              app.assessment_result ? `${app.assessment_result.total_points}/60` : null]
                              .filter(Boolean).join(" · ")}
                          </div>
                        </td>
                        <td className="d-none d-md-table-cell" style={{ fontSize: 14 }}>{app.program?.title || "—"}</td>
                        <td className="d-none d-md-table-cell" style={{ fontSize: 14 }}>{app.district}</td>
                        <td className="d-none d-md-table-cell" style={{ fontSize: 14 }}>{calculateAge(app.dob)}</td>
                        <td className="d-none d-md-table-cell" style={{ fontSize: 14, textTransform: "capitalize" }}>{app.gender || "—"}</td>
                        <td className="d-none d-md-table-cell" style={{ fontSize: 14 }}>
                          {app.assessment_result ? `${app.assessment_result.total_points} / 60` : "—"}
                        </td>
                        <td>
                          {isFullAdmin ? (
                            <button
                              className="btn btn-sm"
                              style={{ backgroundColor: "#4338CA", color: "#fff", borderRadius: 6, fontSize: 13, whiteSpace: "nowrap" }}
                              onClick={() => setSelectedApp(app)}
                            >
                              View Assessments
                            </button>
                          ) : app.sport ? (
                            // Protsaahan: battery test (this stage) then, at
                            // district only, skill test -- label reflects
                            // whichever step is next so the button doesn't
                            // just say "Conduct Assessment" forever.
                            (() => {
                              const label = app.skill_test
                                ? 'View Assessment'
                                : app.battery_test
                                  ? (stageLabel === 'District' ? 'Conduct Skill Test' : 'View Assessment')
                                  : 'Conduct Battery Test';
                              const done = label === 'View Assessment';
                              return (
                                <button
                                  className="btn btn-sm"
                                  style={{ backgroundColor: done ? "#059669" : "#7C3AED", color: "#fff", borderRadius: 6, fontSize: 13, whiteSpace: "nowrap" }}
                                  onClick={() => setSelectedApp(app)}
                                >
                                  {label}
                                </button>
                              );
                            })()
                          ) : app.assessment_result ? (
                            <button
                              className="btn btn-sm"
                              style={{ backgroundColor: "#059669", color: "#fff", borderRadius: 6, fontSize: 13, whiteSpace: "nowrap" }}
                              onClick={() => setSelectedApp(app)}
                            >
                              View Assessment
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm"
                              style={{ backgroundColor: "#7C3AED", color: "#fff", borderRadius: 6, fontSize: 13, whiteSpace: "nowrap" }}
                              onClick={() => setSelectedApp(app)}
                            >
                              Conduct Assessment
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={(canForward || canSelect) ? 9 : 8} className="text-center">No approved applications found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ReactPaginate
        nextLabel="next >"
        onPageChange={handlePageChange}
        pageRangeDisplayed={8}
        marginPagesDisplayed={2}
        pageCount={Math.ceil(total / perPage)}
        previousLabel="< previous"
        pageClassName="page-item mx-1 my-1"
        pageLinkClassName="page-link"
        previousClassName="page-item mx-1 my-1"
        previousLinkClassName="page-link"
        nextClassName="page-item mx-1 my-1"
        nextLinkClassName="page-link"
        breakLabel="..."
        breakClassName="page-item mx-1 my-1"
        breakLinkClassName="page-link"
        containerClassName="pagination d-flex flex-wrap"
        activeClassName="active"
        renderOnZeroPageCount={null}
        forcePage={currentPage - 1}
      />

      {isFullAdmin ? (
        <AssessmentHistoryModal
          show={!!selectedApp}
          onHide={() => setSelectedApp(null)}
          application={selectedApp}
        />
      ) : (
        <AssessmentFormModal
          show={!!selectedApp}
          onHide={(saved) => { setSelectedApp(null); if (saved) fetchData(); }}
          application={selectedApp}
        />
      )}

      {canForward && (
        <ConfirmModal
          type="forward"
          show={showForwardConfirm}
          handleClose={() => setShowForwardConfirm(false)}
          message={`Are you sure you want to send ${selectedIds.length} selected application${selectedIds.length === 1 ? "" : "s"} forward to the next level?`}
          onConfirm={handleSendForward}
        />
      )}

      {canSelect && (
        <ConfirmModal
          type="select"
          show={showSelectConfirm}
          handleClose={() => setShowSelectConfirm(false)}
          message={`Are you sure you want to mark ${selectedIds.length} selected application${selectedIds.length === 1 ? "" : "s"} as Selected? This is a final decision and cannot be undone.`}
          onConfirm={handleSelectApplications}
        />
      )}
    </>
  );
};

export default UddyamaanAssessmentList;
